import { Component, OnInit, OnDestroy, LOCALE_ID, NgZone } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { NgChartsModule } from 'ng2-charts';
import { HeaderComponent } from './components/shared/header/header.component';
import { FooterComponent } from './components/shared/footer/footer.component';
import { SideNavigationMenuComponent } from './components/UI/side-navigation-menu/side-navigation-menu.component';
import { LoginFormComponent } from './components/UI/login-form/login-form.component';
import { HttpClient } from '@angular/common/http';
import { Router, RouterModule } from '@angular/router';
import { SidebarService } from './components/shared/services/sidebar.service';
import { AuthService } from './components/shared/services/auth.service';
import { SetupService } from './components/shared/services/setup.service';
import { Subscription, Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import localeEnZA from '@angular/common/locales/en-ZA';
import { RenewalModalComponent } from './components/shared/renewal-modal/renewal-modal.component';
import { MobileTopBarComponent } from './components/shared/mobile/mobile-top-bar/mobile-top-bar.component';
import { MobileBottomNavComponent } from './components/shared/mobile/mobile-bottom-nav/mobile-bottom-nav.component';
import { MobileMainNavComponent, MobileMainNavLink } from './components/shared/mobile/mobile-main-nav/mobile-main-nav.component';
import { MobileNavService } from './components/shared/mobile/mobile-bottom-nav/mobile-nav.service';
import { ToastComponent } from './components/shared/reusables/toast/toast.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterModule,
    CommonModule,
    NgChartsModule,
    FooterComponent,
    SideNavigationMenuComponent,
    RenewalModalComponent,
    MobileTopBarComponent,
    MobileBottomNavComponent,
    MobileMainNavComponent,
    ToastComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  providers: [
    { provide: LOCALE_ID, useValue: 'en-ZA' }, // Set South African locale globally
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  isSidebarExpanded = false;
  sidebarSubscription: Subscription = new Subscription();
  authSubscription: Subscription = new Subscription();
  setupSubscription: Subscription = new Subscription();
  tokenSubscription: Subscription = new Subscription();
  title = 'AivorAccountingSystem';
  isLoginPage = false;
  isResetPasswordPage = false;
  isForgotPasswordPage = false;
  isInventoryPage = false;
  isRegistrationPage = false;
  isSubscriptionErrorPage = false;
  isRenewSubscriptionPage = false;
  isBusinessDetailsPage = false;
  isConfigSingleViewPage = false;
  showRenewalModal = false;
  currentSubscriptionStatus: string = '';
  isLockActive = true; // Assume lock is active by default until setup status is checked
  isInitialized: boolean = false;
  accountType: string = 'full';

  mobileNavOpen = false;
  mobileNavLinks: MobileMainNavLink[] = [];

  // Modal swipe-to-dismiss state
  private modalSwipeStartY = 0;
  private modalSwipeActive = false;
  private modalSwipeEl: HTMLElement | null = null;
  private isMobileViewport = false;
  private modalMql: MediaQueryList;

  private boundModalTouchStart = this.onModalTouchStart.bind(this);
  private boundModalTouchMove = this.onModalTouchMove.bind(this);
  private boundModalTouchEnd = this.onModalTouchEnd.bind(this);

  constructor(
    private router: Router,
    private sidebarService: SidebarService,
    public authService: AuthService,
    private setupService: SetupService,
    private mobileNavService: MobileNavService,
    private ngZone: NgZone
  ) {
    this.modalMql = window.matchMedia('(max-width: 767.98px)');
    this.isMobileViewport = this.modalMql.matches;
    this.modalMql.addEventListener('change', (e) => this.isMobileViewport = e.matches);
    this.router.events.subscribe(() => {
      this.isLoginPage =
        this.router.url === '/login' || this.router.url === '/';
      this.isResetPasswordPage = this.router.url.startsWith('/reset-password');
      this.isForgotPasswordPage = this.router.url === '/forgot-password';
      this.isInventoryPage = this.router.url === '/inventory';
      this.isRegistrationPage = this.router.url === '/register';
      this.isSubscriptionErrorPage = this.router.url === '/subscription-error';
      this.isRenewSubscriptionPage = this.router.url === '/renew-subscription';
      this.isBusinessDetailsPage = this.router.url.startsWith('/business-details');
      // Check if we're on the config page with view=single (used for adding roles from business-details)
      this.isConfigSingleViewPage = this.router.url.includes('/config') && this.router.url.includes('view=single');

      // Re-read accountType on every route change so UI updates after login/token refresh
      this.accountType = this.authService.getAccountType();

      this.configureMobileBottomNav(this.router.url);
    });
  }

  private buildMobileNavLinks(): void {
    const role = this.authService.getUserRole();
    const type = this.accountType;
    const links: MobileMainNavLink[] = [];

    if (type !== 'calculator') {
      links.push({ label: 'Dashboard', iconClass: 'bi-grid', routerLink: '/dashboard' });
      links.push({ label: 'Inventory', iconClass: 'bi-box-seam', routerLink: '/inventory' });
    }
    if (type !== 'calculator' && (role === 'Admin User' || role === 'Factory User')) {
      links.push({ label: 'Manufacturing', iconClass: 'bi-gear', routerLink: '/manufacturing' });
    }
    if (type === 'calculator' || role === 'Admin User' || role === 'Financial User') {
      links.push({ label: 'Cost Calculator', iconClass: 'bi-calculator', routerLink: '/calc' });
    }
    if (type !== 'calculator' && (role === 'Admin User' || role === 'Financial User')) {
      links.push({ label: 'Budgeting', iconClass: 'bi-clipboard-data', routerLink: '/costing' });
    }
    if (role === 'Admin User') {
      links.push({ label: 'Business Details', iconClass: 'bi-building', routerLink: '/business-details' });
      links.push({ label: 'Administration', iconClass: 'bi-gear-wide-connected', routerLink: '/config' });
    }

    this.mobileNavLinks = links;
  }

  private configureMobileBottomNav(url: string): void {
    // Only show the bottom nav (calculator tabs) on the cost calculator route for now.
    // Other pages will get their own contextual tabs later.
    const isCalc = url.startsWith('/calc');
    const isConfig = url.startsWith('/config');
    const isBusiness = url.startsWith('/business-details');

    if (isCalc) {
      this.mobileNavService.showNav();
      this.mobileNavService.resetTabs();
      return;
    }

    if (url.startsWith('/dashboard')) {
      this.mobileNavService.setTabs([
        { key: 'raw',      label: 'Raw',      icon: 'bi-box-seam' },
        { key: 'products', label: 'Products', icon: 'bi-bag-check' },
        { key: 'fixed',    label: 'Fixed',    icon: 'bi-receipt' },
        { key: 'variable', label: 'Variable', icon: 'bi-graph-up' },
      ], 'raw');
      if (url.includes('view=tables')) {
        this.mobileNavService.showNav();
      } else {
        this.mobileNavService.hideNav();
      }
      return;
    }

    if (url.startsWith('/inventory')) {
      if (url.includes('view=charts') || url.includes('view=import') || !url.includes('view=tables')) {
        this.mobileNavService.hideNav();
      } else {
        this.mobileNavService.showNav();
        this.mobileNavService.setTabs([
          { key: 'raw', label: 'Raw Materials', icon: 'bi-box-seam' },
          { key: 'products', label: 'Products', icon: 'bi-bag-check' },
        ], 'raw');
      }
      return;
    }

    if (isConfig) {
      this.mobileNavService.hideNav();
      return;
    }

    if (isBusiness) {
      if (url.includes('tab=expenses')) {
        this.mobileNavService.showNav();
        this.mobileNavService.setTabs([
          { key: 'variable', label: 'Variable', icon: 'bi-graph-up' },
          { key: 'fixed',    label: 'Fixed',    icon: 'bi-receipt'  },
        ], 'variable');
      } else {
        this.mobileNavService.hideNav();
        this.mobileNavService.resetTabs();
      }
      return;
    }

    if (url.startsWith('/costing')) {
      this.mobileNavService.hideNav();
      this.mobileNavService.resetTabs();
      return;
    }

    if (url.startsWith('/manufacturing')) {
      this.mobileNavService.hideNav();
      this.mobileNavService.resetTabs();
      return;
    }

    this.mobileNavService.hideNav();
  }

  openMobileNav(): void {
    this.mobileNavOpen = true;
  }

  closeMobileNav(): void {
    this.mobileNavOpen = false;
  }

  ngOnInit() {
    try {
      this.authService.ensureTokenInitialized();
      this.isInitialized = true;
    } catch (error) {
      ////////////console.error('Initialization error:', error);
      this.router.navigate(['/login']);
    }
    // Register the locale for South Africa (en-ZA)
    registerLocaleData(localeEnZA);

    // Global swipe-to-dismiss for Bootstrap modals on mobile
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('touchstart', this.boundModalTouchStart, { passive: true });
      document.addEventListener('touchmove', this.boundModalTouchMove, { passive: false });
      document.addEventListener('touchend', this.boundModalTouchEnd, { passive: true });
    });

    this.sidebarSubscription = this.sidebarService.isExpanded$.subscribe(expanded => {
      this.isSidebarExpanded = expanded;
    });

    // Wait for auth service to complete initialization
    this.authService.isInitialized$.pipe(
      filter(initialized => initialized === true),
      take(1) // Only take the first true value
    ).subscribe({
      next: () => {
        // Check account type
        this.accountType = this.authService.getAccountType();

        // Build mobile nav links based on role/accountType (matches desktop side nav)
        this.buildMobileNavLinks();

        // Check setup status on component initialization
        this.checkSetupStatus();

        // Subscribe to setup status changes to update isLockActive
        this.setupSubscription = this.setupService.setupCompleteStatus$.subscribe(isComplete => {
          this.isLockActive = isComplete === null ? true : !isComplete;
          if (isComplete === true) {
            this.accountType = this.authService.getAccountType();
            this.buildMobileNavLinks();
          }
        });

        // Rebuild the mobile nav whenever the token changes (login, silent refresh
        // after setup, renewal). Mirrors how the desktop side-nav re-reads the role
        // on re-instantiation, so role-gated pages appear without a sign-out/in.
        this.tokenSubscription = this.authService.tokenChanged$.subscribe(() => {
          this.accountType = this.authService.getAccountType();
          this.buildMobileNavLinks();
        });

        // Check subscription status for renewal modal
        this.authSubscription = this.authService.subscriptionStatus$.subscribe((status: any) => {
          // Only update modal visibility if user is authenticated
          if (this.authService.getToken()) {
            this.showRenewalModal = status && status !== 'active' && status !== 'trial';
            this.currentSubscriptionStatus = status || '';
          }
        });
      },
      error: (error) => {
        //console.error('Error during auth initialization:', error);
      }
    });
  }

  onRenewalSuccess(): void {
    this.showRenewalModal = false;

    // Force a refresh of the subscription status by getting a new token
    this.authService.refreshToken().subscribe({
      next: () => {
        // Refresh the page to ensure all components get the updated subscription status
        window.location.reload();
      },
      error: (err) => {
        //console.error('Error refreshing token after renewal:', err);
        // Still refresh the page even if token refresh fails
        window.location.reload();
      }
    });
  }

  ngOnDestroy() {
    this.sidebarSubscription.unsubscribe();
    this.authSubscription.unsubscribe();
    this.setupSubscription.unsubscribe();
    this.tokenSubscription.unsubscribe();

    document.removeEventListener('touchstart', this.boundModalTouchStart);
    document.removeEventListener('touchmove', this.boundModalTouchMove);
    document.removeEventListener('touchend', this.boundModalTouchEnd);
  }

  // ── Modal swipe-to-dismiss handlers ──────────────────────────────
  private onModalTouchStart(e: TouchEvent): void {
    if (!this.isMobileViewport) return;

    const modalEl = document.querySelector('.modal.show') as HTMLElement | null;
    if (!modalEl) return;

    // Only initiate swipe from the handle area (top ~40px of modal-content) or modal-header
    const target = e.target as HTMLElement;
    const modalContent = modalEl.querySelector('.modal-content') as HTMLElement | null;
    if (!modalContent) return;

    const contentRect = modalContent.getBoundingClientRect();
    const touchY = e.touches[0].clientY;

    // Allow swipe from handle area (::before pseudo = top 20px) or from .modal-header
    const isInHandleArea = touchY >= contentRect.top && touchY <= contentRect.top + 40;
    const isInHeader = !!target.closest('.modal-header');

    if (!isInHandleArea && !isInHeader) return;

    this.modalSwipeActive = true;
    this.modalSwipeStartY = e.touches[0].clientY;
    this.modalSwipeEl = modalContent;
    modalContent.style.transition = 'none';
  }

  private onModalTouchMove(e: TouchEvent): void {
    if (!this.modalSwipeActive || !this.modalSwipeEl) return;

    const delta = e.touches[0].clientY - this.modalSwipeStartY;
    if (delta < 0) return; // only allow downward swipe

    e.preventDefault();
    this.modalSwipeEl.style.transform = `translateY(${delta}px)`;
    this.modalSwipeEl.style.opacity = `${1 - (delta / (this.modalSwipeEl.offsetHeight * 0.3)) * 0.3}`;
  }

  private onModalTouchEnd(e: TouchEvent): void {
    if (!this.modalSwipeActive || !this.modalSwipeEl) return;

    const delta = e.changedTouches[0].clientY - this.modalSwipeStartY;
    const threshold = this.modalSwipeEl.offsetHeight * 0.3;

    this.modalSwipeEl.style.transition = 'transform 0.3s ease, opacity 0.3s ease';

    if (delta > threshold) {
      // Dismiss: slide fully down then close
      this.modalSwipeEl.style.transform = `translateY(100%)`;
      this.modalSwipeEl.style.opacity = '0';

      const modalBackdrop = this.modalSwipeEl.closest('.modal.show') as HTMLElement | null;
      if (modalBackdrop) {
        // Use Bootstrap API if available, fallback to clicking close button
        const bsModal = (window as any).bootstrap?.Modal?.getInstance(modalBackdrop);
        if (bsModal) {
          setTimeout(() => { this.ngZone.run(() => bsModal.hide()); }, 200);
        } else {
          const closeBtn = modalBackdrop.querySelector('[data-bs-dismiss="modal"], .btn-close, .close') as HTMLElement | null;
          if (closeBtn) {
            setTimeout(() => { this.ngZone.run(() => closeBtn.click()); }, 200);
          }
        }
      }
    } else {
      // Snap back
      this.modalSwipeEl.style.transform = '';
      this.modalSwipeEl.style.opacity = '';
    }

    this.modalSwipeActive = false;
    this.modalSwipeEl = null;
  }

  private checkSetupStatus(): void {
    const token = this.authService.getToken();
    if (token) {
      this.setupService.checkSetupStatus().subscribe({
        next: (response: any) => {
          this.isLockActive = response.isSetupComplete === null ? true : !response.isSetupComplete;
        },
        error: (error) => {
          //console.error('Error checking setup status:', error);
          this.isLockActive = true; // Default to locked on error
        }
      });
    } else {
      this.isLockActive = true; // Lock if not authenticated
    }
  }
}
